import { useMutation, useQueryClient } from '@tanstack/react-query';

import { messages as dm } from '../messages/api';
import { MY_JAM_KEY } from './api';
import { startJam, useMyJam } from './useJam';

/**
 * "Jam" on a conversation: invite this person into the jam you are in, or
 * start one and invite them in the same press.
 *
 * One button for both because the difference is not one the sender should
 * have to think about. Either way the thread gets a card with Join on it,
 * and the sender ends up in a jam with a queue waiting for tracks.
 */
export function JamInviteButton({
  conversationId,
  compact = false,
}: {
  conversationId: number;
  compact?: boolean;
}) {
  const client = useQueryClient();
  const { data } = useMyJam();
  const current = data?.jam ?? null;

  const invite = useMutation({
    mutationFn: async () => {
      const jam = current ?? (await startJam(client));

      await dm.send(conversationId, { jam_id: jam.id });

      return jam;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: MY_JAM_KEY });
      void client.invalidateQueries({ queryKey: ['thread', conversationId] });
      void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void client.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  return (
    <button
      type="button"
      onClick={() => invite.mutate()}
      disabled={invite.isPending}
      title={current ? 'Invite to your jam' : 'Start a jam together'}
      aria-label={current ? 'Invite to your jam' : 'Start a jam together'}
      data-jam-invite={current ? 'invite' : 'start'}
      className={`flex shrink-0 items-center gap-1.5 rounded-[var(--radius-pill)] border border-line font-mono text-soft transition-colors hover:border-line-lit hover:text-ink disabled:opacity-40 ${
        compact ? 'h-7 px-2 text-[10px]' : 'px-2.5 py-1 text-[11px]'
      }`}
    >
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <path d="M3 12V5.5l7-1.5v6.5M3 12a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0Zm7-1.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0Z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {invite.isPending ? '…' : current ? 'invite to jam' : 'jam'}
    </button>
  );
}
